import { Box, Button, Grid, IconButton, InputBase, Skeleton, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { compressImage, isFileNameImage, readFileToDataUrl } from "../../../common/mission";
import { makeId } from "@emberly/zenith-client";
import { Cancel as CloseIcon, UploadFile as UploadIcon } from "@mui/icons-material";
import { useNotification } from "../../../providers/NotificationProvider";
import { useDevice } from "../../../providers/DeviceProvider";

const useStyles = makeStyles()(
  (theme) => ({
    content: {
      position: "relative",
      borderRadius: theme.spacing(1),
      width: "100%",
      height: "auto",
      background: theme.palette.background.default,
      padding: theme.spacing(2)
    },
    isMobilePortraitView: {
      padding: theme.spacing(1.5)
    },
    contentDragOver: {
      outline: `2px dashed ${theme.palette.divider}`
    },
    uploadButton: {
      border: `1px solid ${theme.palette.divider}`
    },
    imageContainer: {
      position: "relative",
      height: "64px",
      width: "auto",
    },
    image: {
      borderRadius: theme.spacing(1),
      position: "relative",
      height: "100%",
      width: "auto",
      minWidth: "96px",
      objectFit: "cover",
      outline: `1px solid ${theme.palette.divider}`
    },
    skeleton: {
      borderRadius: theme.spacing(1),
      position: "relative",
      height: "100%",
      width: "96px"
    },
    closeButton: {
      position: "absolute",
      top: theme.spacing(-1),
      right: theme.spacing(-1),
      zIndex: 2,
      padding: 0,
      "& svg": {
        padding: 0,
        borderRadius: "50%",
        background: theme.palette.background.default,
      }
    }
  })
);

export default function MultiModalInput(props) { // TODO add support for images etc
  const { placeholder, imageLimit, imageLimitText, imageSizeLimitText, textLimitText, onChange, apiRef, disabled, working, onConfirm, error } = props;
  const { classes } = useStyles();
  const { snackbar } = useNotification();
  const [dragOver, setDragOver] = useState(0);
  const { isMobilePortraitView } = useDevice();

  const [text, setText] = useState("");
  const [images, setImages] = useState([]);

  const isEmpty = text === "" && images.filter(t => !t.loading).length === 0;

  const onChangeText = useCallback((ev) => {
    let t = ev.target.value;

    if (t.length > 16000) {
      t = t.substring(0, 16000);
      snackbar(textLimitText);
    }

    setText(t);

    if (typeof onChange === "function") {
      onChange({ text: t, images });
    }

  }, [images, snackbar, textLimitText, onChange]);

  const onRemoveImage = useCallback((img) => {
    let list = images.filter(t => t.id !== img.id);
    setImages(list);

    if (typeof onChange === "function") {
      onChange({ text, images: list });
    }
  }, [images, onChange, text])

  const addImages = useCallback(async (files) => {


    if (images.length + files.length > imageLimit) {
      snackbar(imageLimitText);
      return;
    }


    let mappedFiles = files.map(t => ({ loading: true, file: t, id: makeId(), src: "" }));
    setImages(list => [...list, ...mappedFiles]);

    for (let i = 0; i < files.length; i++) {
      const f = mappedFiles[i];
      const img = await compressImage(f.file);

      if (img.size > 3 * 1024 * 1024) {
        snackbar(imageSizeLimitText);
        setImages(list => list.filter(t => t.id !== f.id));
      } else {
        f.src = await readFileToDataUrl(img);
        f.loading = false;
        f.file = img;
      }

      setImages(list => [...list]);
    }

    if (typeof onChange === "function") {
      setImages(list => {
        setTimeout(() => {
          onChange({ text, images: list });
        }, 1)
        return list;
      });
    }

  }, [images, text, onChange, imageLimit, imageLimitText, imageSizeLimitText, snackbar]);

  if (apiRef) {
    apiRef.current = addImages;
  }

  const onPaste = useCallback((ev) => {
    const files = [...ev.clipboardData.files].filter(t => isFileNameImage(t.name));

    if (files.length !== 0) {
      ev.preventDefault();
      addImages(files);
    }
  }, [addImages]);


  const onDragEnter = useCallback((ev) => {
    try {
      setDragOver(t => t + 1);
    } catch (err) {
      console.log(err)
      setDragOver(t => t + 1);
    }
  }, [setDragOver]);

  const onDragLeave = useCallback((ev) => {
    try {
      setDragOver(t => Math.max(t - 1, 0));
    } catch (err) {
      console.log(err)
      setDragOver(t => Math.max(t - 1, 0));
    }
  }, [setDragOver]);

  const onDragOver = useCallback((ev) => ev.preventDefault(), []);

  const onFileDropped = useCallback(async (ev) => {
    setDragOver(0);

    const files = !!ev?.dataTransfer?.files ? [...ev.dataTransfer.files].filter(t => isFileNameImage(t.name)) : [];

    if (files.length !== 0 && !!files[0]) {
      ev.preventDefault();
      addImages(files);
    }

  }, [setDragOver, addImages]);

  const onHandleFileSelect = useCallback((ev) => {
    const files = [...ev.target.files].filter(t => isFileNameImage(t.name));

    if (files.length !== 0) {
      addImages(files);
    }
  }, [addImages]);

  return (
    <Grid container spacing={2}>

      <Grid item xs={12}>
        <Box
          className={`${classes.content} ${isMobilePortraitView ? classes.isMobilePortraitView : ""} ${dragOver ? classes.contentDragOver : ""}`}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onFileDropped}
          onDragOver={onDragOver}
        >
          <Grid container spacing={1}>

            {images.map((img, key) => (
              <Grid item key={key}>
                <ImagePreview
                  loading={img.loading}
                  src={img.src}
                  onClose={onRemoveImage}
                  img={img}
                  disabled={disabled}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <InputBase
                disabled={disabled}
                placeholder={placeholder}
                value={text}
                onChange={onChangeText}
                fullWidth
                multiline
                onPaste={onPaste}
              />
              {!!error ? (<Typography color="error" variant="body2">{error}</Typography>) : null}
            </Grid>


          </Grid>
        </Box>
      </Grid>


      <Grid item xs={12}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <IconButton className={classes.uploadButton} size="small" component="label" disabled={working}>
              <input
                type="file"
                hidden
                multiple              // or remove if you only want one
                accept="image/*"      // optional filter
                onChange={onHandleFileSelect}
              />
              <UploadIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" disabled={isEmpty} loading={working} onClick={onConfirm}>
              Generer forslag
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}


function ImagePreview(props) {
  const { src, loading, onClose, img, disabled } = props;
  const { classes } = useStyles();

  return (
    <Box className={classes.imageContainer}>

      <IconButton className={classes.closeButton} color="primary" size="small" onClick={() => onClose(img)} disabled={disabled}>
        <CloseIcon />
      </IconButton>

      {
        loading ? (
          <Skeleton className={classes.skeleton} variant="rectangular" />
        ) : (
          <img src={src} className={classes.image} />
        )
      }

    </Box>
  )
}